import React, { useState } from 'react';
import { MessageCircle, Loader2 } from 'lucide-react';
import { extractTextWithoutBase64 } from '../../utils/htmlTextExtractor';

interface FeedbackBubbleProps {
  // Presence flag only — cheap to include in list/search results, unlike the HTML itself
  // (which is now stored in blob storage and fetched lazily on hover).
  hasFeedback?: boolean | null;
  // Fetches the full feedback HTML for this row. Only called once per row, on first hover.
  fetchFeedback: () => Promise<string | null | undefined>;
}

const maxPreviewLength = 150;

const FeedbackBubble: React.FC<FeedbackBubbleProps> = ({ hasFeedback, fetchFeedback }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  if (!hasFeedback) {
    return <span>-</span>;
  }

  const handleMouseEnter = () => {
    setIsHovered(true);

    if (hasFetched) return;

    setIsLoading(true);
    fetchFeedback()
      .then((html) => {
        setPreviewText(extractTextWithoutBase64(html, maxPreviewLength));
      })
      .catch(() => {
        setPreviewText('Unable to load feedback');
      })
      .finally(() => {
        setIsLoading(false);
        setHasFetched(true);
      });
  };

  return (
    <div className="relative inline-block">
      <div
        className="cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovered(false)}
      >
        <MessageCircle className="h-4 w-4 text-blue-500" />
      </div>

      {isHovered && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 max-w-xs">
          <div className="bg-gray-800 text-white text-sm rounded-lg p-3 shadow-lg">
            {isLoading ? (
              <div className="flex items-center justify-center py-1">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : (
              <div className="whitespace-pre-wrap break-words">{previewText || '-'}</div>
            )}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-8 border-transparent border-t-gray-800"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackBubble;
